import { gql, useQuery } from '@apollo/client';

import type { GetVehiclesQuery, GetVehiclesQueryVariables } from './__generated__/useVehicles.gql';
import type { VehicleOwnership } from '../../__generated__/globalTypes';

const GET_VEHICLES = gql`
  query GetVehicles {
    getCurrentFamily {
      id
      vehicles {
        id
        year
        make
        model
        ownership
        ownershipOther
      }
    }
  }
`;

export interface IVehicle {
  id: string;
  year: number | null;
  make: string | null;
  model: string | null;
  ownership: VehicleOwnership;
  ownershipOther: string | null;
}

export interface IUseVehiclesResult {
  /** Family-scoped vehicles in API order (`VehicleController.getAllByFamilyId` sorts by `createdAt`, `id`). */
  vehicles: IVehicle[];
  loading: boolean;
  /** Refetch the list — call after a create/delete so the new/removed row is reflected. */
  refetch: () => Promise<unknown>;
}

/**
 * Reads the acting user's family vehicles — the single vehicle list query used
 * by both the Possessions settings tab (make/model + ownership) and the
 * onboarding vehicle step (ownership only). In-place make/model/ownership edits
 * ride back via a mutation's returned fields and Apollo's normalized cache; a
 * create/delete changes list membership, so it needs an explicit refetch — via
 * {@link IUseVehiclesResult.refetch} or the mutation's own `refetchQueries`.
 */
export const useVehicles = (): IUseVehiclesResult => {
  const { data, loading, refetch } = useQuery<GetVehiclesQuery, GetVehiclesQueryVariables>(GET_VEHICLES, {
    fetchPolicy: 'cache-and-network',
  });

  const family = data?.getCurrentFamily ?? null;
  if (!family) {
    return { vehicles: [], loading, refetch };
  }

  const vehicles: IVehicle[] = family.vehicles.map((v) => ({
    id: v.id,
    year: v.year ?? null,
    make: v.make ?? null,
    model: v.model ?? null,
    ownership: v.ownership,
    ownershipOther: v.ownershipOther ?? null,
  }));

  return { vehicles, loading, refetch };
};
